/* Shared "which size fits my dog" logic, used by the product page's size finder
 * (assets/product-size-finder.js) and the product cards' size picker (assets/product-card.js), so a
 * recommendation is identical wherever it is shown.
 *
 * Measurements are whole cm keyed by a language independent key ({ back, neck, chest }) and are
 * remembered in localStorage under STORAGE_KEY ({ v: 1, m: { ... } }).
 */
(() => {
  if (window.CowalkiesSizeFit) return;

  const STORAGE_KEY = 'cw_dog_size';
  const SIZES = ['xs', 's', 'm', 'l'];

  const read = () => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (data && data.v === 1 && data.m && typeof data.m === 'object') return data.m;
    } catch (e) {
      /* storage unavailable or corrupt: behave as if nothing was saved */
    }
    return null;
  };

  const write = (m) => {
    try {
      if (m) localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, m }));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* private mode etc.: the feature still works for this page view */
    }
  };

  /* Which size index fits one measurement. ranges: { xs: { back: [21, 26], ... }, s: {...}, ... }.
     A dog is in a size when its value lies inside that size's [from, to] for the measurement; sizes
     overlap on purpose, so several can match. Returns { i, between, over } or null when no size has a
     range for this measurement.
       - inside one or more ranges: the largest of them (rule 'larger') or the smallest ('smaller');
         "between" is true when more than one matched
       - between two ranges (a gap): the size above ('larger') or below ('smaller'); between is true
       - below the smallest range: the first size; above the largest: the last size, flagged "over" */
  const pickIndex = (ranges, key, dog, rule) => {
    const spans = SIZES.map((size) => {
      const r = ranges[size] && ranges[size][key];
      const lo = r && parseFloat(r[0]);
      const hi = r && parseFloat(r[1]);
      return Number.isFinite(lo) && Number.isFinite(hi) ? [lo, hi] : null;
    });
    const known = spans.map((r, i) => (r ? i : -1)).filter((i) => i > -1);
    if (!known.length) return null;
    const inside = known.filter((i) => dog >= spans[i][0] && dog <= spans[i][1]);
    if (inside.length) {
      const i = rule === 'smaller' ? inside[0] : inside[inside.length - 1];
      return { i, between: inside.length > 1, over: false };
    }
    const first = known[0];
    const last = known[known.length - 1];
    if (dog < spans[first][0]) return { i: first, between: false, over: false, under: true };
    if (dog > spans[last][1]) return { i: last, between: false, over: true };
    const below = known.filter((i) => spans[i][1] < dog).pop();
    const above = known.find((i) => spans[i][0] > dog);
    return { i: rule === 'smaller' ? below : above, between: true, over: false };
  };

  /* ranges: the product's dog size ranges, rule: 'larger' | 'smaller' (which way to go when a dog
     fits more than one size), measurements: { back: 27, chest: 40, ... } whole cm. Only measurements
     that appear in the ranges count (neck, for instance, is collected but ignored). The biggest size
     any single measurement needs wins. Returns { size, between, over } or null. */
  const recommend = (ranges, rule, measurements) => {
    if (!ranges || !measurements) return null;
    const keys = new Set();
    SIZES.forEach((size) => ranges[size] && Object.keys(ranges[size]).forEach((k) => keys.add(k)));
    let best = null;
    let over = false;
    let picks = 0;
    let unders = 0;
    keys.forEach((key) => {
      const dog = measurements[key];
      if (!Number.isFinite(dog)) return;
      const pick = pickIndex(ranges, key, dog, rule);
      if (!pick) return;
      picks++;
      if (pick.under) unders++;
      if (pick.over) over = true;
      if (!best || pick.i > best.i) best = pick;
    });
    if (!best) return null;
    // under: every measurement given is below our smallest size (over: any is above our largest).
    return { size: SIZES[best.i].toUpperCase(), between: best.between && !over, over, under: !over && unders === picks };
  };

  /* Allowed entry range for one measurement: the product's own smallest and largest range for it, plus
     a margin (cm) either side for a dog a little under the first size or over the last. Returns
     [min, max] or null when the product has no range for this measurement. */
  const limits = (ranges, key, margin) => {
    const vals = [];
    SIZES.forEach((size) => {
      const r = ranges && ranges[size] && ranges[size][key];
      if (r) vals.push(parseFloat(r[0]), parseFloat(r[1]));
    });
    const nums = vals.filter((v) => Number.isFinite(v));
    if (!nums.length) return null;
    return [Math.max(1, Math.floor(Math.min(...nums)) - margin), Math.ceil(Math.max(...nums)) + margin];
  };

  window.CowalkiesSizeFit = { STORAGE_KEY, SIZES, read, write, recommend, limits };
})();
